'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

const SCROLL_THRESHOLD = 100

export function useIdleHide(timeoutMs: number, disabled?: boolean) {
  const [idle, setIdle] = useState(false)
  const lastScrollY = useRef(0)
  const scrollDelta = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearHideTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current)
      timer.current = null
    }
  }, [])

  const scheduleHide = useCallback(() => {
    clearHideTimer()
    timer.current = setTimeout(() => setIdle(true), timeoutMs)
  }, [clearHideTimer, timeoutMs])

  const resetIdleHide = useCallback(() => {
    setIdle(false)
    if (!disabled) scheduleHide()
  }, [disabled, scheduleHide])

  useEffect(() => {
    if (disabled) {
      clearHideTimer()
      setIdle(false)
      return
    }

    const onScroll = () => {
      const currentScrollY = window.scrollY
      scrollDelta.current += currentScrollY - lastScrollY.current
      lastScrollY.current = currentScrollY

      if (scrollDelta.current > SCROLL_THRESHOLD) {
        scrollDelta.current = 0
        clearHideTimer()
        setIdle(true)
      } else if (scrollDelta.current < -SCROLL_THRESHOLD) {
        scrollDelta.current = 0
        setIdle(false)
        scheduleHide()
      }
    }

    scheduleHide()
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })

    return () => {
      clearHideTimer()
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
  }, [clearHideTimer, disabled, scheduleHide])

  return { isIdle: idle, resetIdleHide }
}
